import { X } from "lucide-react";
import React from "react";
import { useModalFocusTrap } from "../useModalFocusTrap";

interface AdminModalShellProps {
  appearance?: "default" | "alpha";
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidthClassName?: string;
  contentClassName?: string;
  closeDisabled?: boolean;
  closeLabel?: string;
}

const AdminModalShell: React.FC<AdminModalShellProps> = ({
  appearance = "default",
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  maxWidthClassName = "max-w-lg",
  contentClassName = "px-6 py-4",
  closeDisabled = false,
  closeLabel = "Close modal",
}) => {
  const titleId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);
  useModalFocusTrap({ closeDisabled, dialogRef, isOpen, onClose });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 py-8 text-center">
        <button
          type="button"
          className={appearance === "alpha" ? "fixed inset-0 bg-v7-ink/60" : "fixed inset-0 bg-black/60"}
          onClick={closeDisabled ? undefined : onClose}
          aria-label="Close modal backdrop"
        />

        <div
          ref={dialogRef}
          data-appearance={appearance}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          className={appearance === "alpha" ? `relative inline-block w-full ${maxWidthClassName} overflow-hidden rounded-v7-popover border border-v7-ink/15 bg-v7-white font-v7-sans text-v7-ink text-left align-middle shadow-lg transition-colors duration-300 ` : `relative inline-block w-full ${maxWidthClassName} overflow-hidden rounded-lg border border-gray-200 bg-white text-left align-middle shadow-lg transition-colors duration-300 dark:border-rp-separator dark:bg-rp-surface-raised`}
        >
          <div className={appearance === "alpha" ? "flex items-start justify-between gap-4 border-b border-v7-ink/15 px-6 py-4" : "flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-rp-separator"}>
            <div>
              <h2 id={titleId} className={appearance === "alpha" ? "text-base font-bold leading-tight text-v7-ink" : "text-base font-bold leading-tight text-gray-900 dark:text-white"}>
                {title}
              </h2>
              {description && (
                <div className={appearance === "alpha" ? "mt-1 text-sm text-v7-ink-soft" : "mt-1 text-sm text-gray-600 dark:text-gray-300"}>
                  {description}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={closeDisabled}
              className={appearance === "alpha" ? "rounded-v7-panel p-1 text-v7-ink/60 transition-colors hover:bg-v7-paper hover:text-v7-ink-soft disabled:cursor-not-allowed disabled:opacity-50" : "rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-rp-surface dark:hover:text-gray-300"}
              aria-label={closeLabel}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className={`max-h-[70vh] overflow-y-auto ${contentClassName}`}>
            {children}
          </div>

          {footer && (
            <div className={appearance === "alpha" ? "border-t border-v7-ink/15 px-6 py-4" : "border-t border-gray-200 px-6 py-4 dark:border-rp-separator"}>
              {footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminModalShell;
